// components/DrawingHistoryDialog.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography,
  IconButton, CircularProgress, Button, Divider, Tooltip,
  List, ListItem, Chip, InputAdornment, TextField,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { loadCloudDrawingList, loadDrawingData, deleteDrawingFromCloud } from '../services/drawingStorage';

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(date) {
  if (!date) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(date);
}

export default function DrawingHistoryDialog({ open, onClose, uid, onOpenDrawing }) {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  const filteredList = search.trim()
    ? list.filter((e) => (e.fileName || '').toLowerCase().includes(search.trim().toLowerCase()))
    : list;

  const fetchList = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    setError(null);
    try {
      const items = await loadCloudDrawingList(uid);
      setList(items);
    } catch (e) {
      setError('Не удалось загрузить историю чертежей');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const handleOpen = async (entry) => {
    setLoadingId(entry.id);
    try {
      const data = await loadDrawingData(entry);
      onOpenDrawing(data, entry.fileName);
      onClose();
    } catch (e) {
      setError('Не удалось открыть чертёж');
      console.error(e);
    } finally {
      setLoadingId(null);
    }
  };

  const handleDelete = async (entry) => {
    setDeletingId(entry.id);
    try {
      await deleteDrawingFromCloud(uid, entry);
      setList((prev) => prev.filter((i) => i.id !== entry.id));
    } catch (e) {
      setError('Не удалось удалить чертёж');
      console.error(e);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = async (entry) => {
    setLoadingId(entry.id);
    try {
      const data = await loadDrawingData(entry);
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${entry.fileName}.ptCAD`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Не удалось скачать чертёж');
      console.error(e);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '0.75rem',
          overflow: 'hidden',
          boxShadow: '0 1.5rem 4rem rgba(0,0,0,0.15)',
        }
      }}
    >
      {/* Шапка */}
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 3, py: 2,
          background: '#fff',
          borderBottom: '0.0625rem solid #e0e0e0',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: '2.25rem', height: '2.25rem',
              borderRadius: '0.5rem',
              background: 'linear-gradient(135deg, #d32f2f, #e53935)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <HistoryIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#212121', lineHeight: 1.2 }}>
                История чертежей
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', lineHeight: 1 }}>
                Все сохранённые чертежи вашего аккаунта
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: '#9e9e9e', '&:hover': { color: '#212121', background: '#f5f5f5' } }}>
            ✕
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ px: 2.5, py: 1.5, background: '#fff', borderBottom: '0.0625rem solid #f0f0f0' }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Поиск по названию..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '1rem', color: '#9e9e9e' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.25 }}>
                  <ClearIcon sx={{ fontSize: '0.9rem', color: '#9e9e9e' }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '0.5rem',
              fontSize: '0.82rem',
              '& fieldset': { borderColor: '#e8e8e8' },
              '&:hover fieldset': { borderColor: '#bdbdbd' },
              '&.Mui-focused fieldset': { borderColor: '#d32f2f', borderWidth: '0.0625rem' },
            },
          }}
        />
      </Box>

      <DialogContent sx={{ p: 0, background: '#fafafa', minHeight: '20rem' }}>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6, flexDirection: 'column', gap: 1.5 }}>
            <CircularProgress size={32} sx={{ color: '#d32f2f' }} />
            <Typography sx={{ fontSize: '0.8rem', color: '#9e9e9e' }}>Загрузка истории...</Typography>
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ fontSize: '0.85rem', color: '#d32f2f' }}>{error}</Typography>
            <Button size="small" onClick={fetchList} sx={{ mt: 1 }}>Повторить</Button>
          </Box>
        )}

        {!loading && !error && list.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6, flexDirection: 'column', gap: 1.5 }}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: '3rem', color: '#e0e0e0' }} />
            <Typography sx={{ fontSize: '0.85rem', color: '#bdbdbd' }}>
              Нет сохранённых чертежей
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#d0d0d0', textAlign: 'center', maxWidth: '18rem' }}>
              При сохранении чертежа в формате .ptCAD он автоматически попадёт в историю
            </Typography>
          </Box>
        )}

        {!loading && !error && list.length > 0 && filteredList.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6, flexDirection: 'column', gap: 1 }}>
            <SearchIcon sx={{ fontSize: '2.5rem', color: '#e0e0e0' }} />
            <Typography sx={{ fontSize: '0.85rem', color: '#bdbdbd' }}>
              Ничего не найдено
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#d0d0d0' }}>
              Попробуйте изменить запрос
            </Typography>
          </Box>
        )}

        {!loading && !error && filteredList.length > 0 && (
          <List disablePadding>
            {filteredList.map((entry, idx) => (
              <React.Fragment key={entry.id}>
                <ListItem
                  sx={{
                    px: 2.5, py: 1.5,
                    background: '#fff',
                    '&:hover': { background: '#fafafa' },
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  {/* Превью чертежа */}
                  <Tooltip
                    title={entry.thumbnail
                      ? <Box component="img" src={entry.thumbnail} alt="preview"
                          sx={{ display: 'block', width: '16rem', height: 'auto', borderRadius: '0.4rem' }} />
                      : 'Нет превью'
                    }
                    placement="right"
                    arrow={false}
                    componentsProps={{ tooltip: { sx: { background: '#fff', border: '0.0625rem solid #e0e0e0', borderRadius: '0.5rem', p: entry.thumbnail ? 0.5 : 1, boxShadow: '0 0.5rem 2rem rgba(0,0,0,0.15)', maxWidth: 'unset' } } }}
                  >
                    <Box sx={{
                      width: '3.5rem', height: '2.5rem', flexShrink: 0,
                      borderRadius: '0.4rem',
                      border: '0.0625rem solid #e8e8e8',
                      overflow: 'hidden',
                      background: '#f9f9f9',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'default',
                    }}>
                      {entry.thumbnail
                        ? <Box component="img" src={entry.thumbnail} alt={entry.fileName}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        : <InsertDriveFileOutlinedIcon sx={{ fontSize: '1.2rem', color: '#d32f2f', opacity: 0.5 }} />
                      }
                    </Box>
                  </Tooltip>

                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.fileName || 'Без названия'}<Box component="span" sx={{ fontFamily: 'monospace', color: '#9e9e9e', fontWeight: 400 }}>.ptCAD</Box>
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#9e9e9e' }}>{formatDate(entry.savedAt)}</Typography>
                      <Chip label={formatBytes(entry.byteSize)} size="small" sx={{ height: '1.1rem', fontSize: '0.65rem', background: '#f5f5f5', color: '#9e9e9e' }} />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title="Открыть в редакторе">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleOpen(entry)}
                          disabled={loadingId === entry.id || deletingId === entry.id}
                          sx={{ color: '#d32f2f', '&:hover': { background: 'rgba(211,47,47,0.08)' } }}
                        >
                          {loadingId === entry.id
                            ? <CircularProgress size={16} sx={{ color: '#d32f2f' }} />
                            : <FolderOpenIcon sx={{ fontSize: '1.1rem' }} />
                          }
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Скачать .ptCAD">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(entry)}
                          disabled={loadingId === entry.id || deletingId === entry.id}
                          sx={{ color: '#616161', '&:hover': { background: '#f5f5f5' } }}
                        >
                          <FileDownloadIcon sx={{ fontSize: '1.1rem' }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Удалить из истории">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(entry)}
                          disabled={loadingId === entry.id || deletingId === entry.id}
                          sx={{ color: '#bdbdbd', '&:hover': { color: '#d32f2f', background: 'rgba(211,47,47,0.06)' } }}
                        >
                          {deletingId === entry.id
                            ? <CircularProgress size={16} sx={{ color: '#d32f2f' }} />
                            : <DeleteOutlineIcon sx={{ fontSize: '1.1rem' }} />
                          }
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </ListItem>
                {idx < filteredList.length - 1 && <Divider sx={{ mx: 2.5 }} />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <Box sx={{
        background: '#fff',
        borderTop: '0.0625rem solid #e0e0e0',
        px: 3, py: 1.25,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Typography sx={{ fontSize: '0.7rem', color: '#bdbdbd' }}>
          {list.length > 0
          ? search.trim()
            ? `Найдено: ${filteredList.length} из ${list.length}`
            : `${list.length} чертёж(ей) в истории`
          : ''
        }
        </Typography>
        <Button
          size="small"
          onClick={onClose}
          variant="outlined"
          sx={{ fontSize: '0.75rem', borderColor: '#e0e0e0', color: '#616161', '&:hover': { borderColor: '#bdbdbd', background: '#f5f5f5' } }}
        >
          Закрыть
        </Button>
      </Box>
    </Dialog>
  );
}
